import * as React from 'react';
import { useEffect, useState } from 'react';
import { Routes, useJourneyNavigation } from '../../../routes';
import { AccessColumnEvent, TableRow } from '../../../widgets/table/TableWidget';
import { PageHeadingWidget } from '../../../widgets/page/PageHeadingWidget';
import { Route, Routes as RoutesSwitch } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { useSystem } from '../../../hooks/useSystem';
import { SearchableTableWidget } from '../../../widgets/table/SearchableTableWidget';
import { IntegrationModel } from '@journeyapps-platform/lib-journey-core';
import {
  GithubAccountType,
  IntegrationKind,
  IntegrationState,
  ReturnUrlResult
} from '@journeyapps-platform/types-cerberus';
import { styled } from '../../../theme';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonType, ButtonWidget } from '../../../widgets/buttons/ButtonWidget';
import { TooltipPosition } from '../../../utils/Tooltips';
import { AddIntegrationDialog } from './AddIntegrationDialog';
import { useAllowed } from '../../../hooks/useAllowed';
import { IntegrationInstallationDialogWidget } from './IntegrationInstallationDialogWidget';
import { OrgPageWidget } from '../../../widgets/page/OrgPageWidget';
import { IntegrationModelAction } from '@journeyapps-platform/cardinal-catalog';

const azure_devops = require('../../../../media/azure_devops.png');

export interface IntegrationRow extends TableRow {
  integration: IntegrationModel;
}

export const Meta: React.FC<{ label: string; value: string }> = (props) => {
  return (
    <S.Meta>
      <S.MetaLabel>{props.label}</S.MetaLabel>
      <S.MetaValue>{props.value}</S.MetaValue>
    </S.Meta>
  );
};

export const DeleteIntegrationButton: React.FC<{
  event: AccessColumnEvent<IntegrationRow>;
  integration: IntegrationModel;
}> = (props) => {
  const { event, integration } = props;
  const system = useSystem();
  const isAllowedToDeleteIntegration = useAllowed({
    action: IntegrationModelAction.DELETE,
    resource: event.row.integration
  });
  return (
    <S.RowButton
      icon={'trash'}
      disabled={!isAllowedToDeleteIntegration}
      tooltip={isAllowedToDeleteIntegration ? 'Delete integration' : 'Not permitted'}
      type={ButtonType.SECONDARY}
      tooltipPos={TooltipPosition.BOTTOM}
      action={async () => {
        const ok = await system.dialogStore.confirmDialog({
          title: 'Please confirm',
          message: (
            <>
              Are you sure you want to delete this integration? <br />
              <br />
              Warning: All projects using this integration will fail to load in the IDE/dashboard once the integration
              is deleted
            </>
          )
        });
        if (!ok) {
          return;
        }
        try {
          await integration.delete();
        } catch (e) {
          console.error(`Failed to delete integration with id:${integration.model.id}`, e);
        }
      }}
    />
  );
};

export const IntegrationsPage: React.FC = observer(() => {
  const orgStore = useOrgStore();
  const { navigate, params, relativeRoute } = useJourneyNavigation(Routes.MANAGE_INTEGRATIONS);
  const system = useSystem();

  const [tempIntegration] = useState<IntegrationModel>(
    system.integrationStore.generate({
      id: '',
      org_id: params.org_id,
      properties: {},
      state: IntegrationState.Pending
    } as any)
  );
  const isAllowedAddIntegration = useAllowed({ action: IntegrationModelAction.CREATE, resource: tempIntegration });

  // notices for the various
  useEffect(() => {
    (async () => {
      const url = new URL(window.location.href);
      const installationResult = url.searchParams?.get('result') as ReturnUrlResult;

      // nothing to do here
      if (!installationResult) {
        return;
      }
      if (installationResult === ReturnUrlResult.Connected) {
        await system.dialogStore.infoDialog({
          title: 'Installation complete',
          message: 'The integration was installed successfully.'
        });
      }
      if (installationResult === ReturnUrlResult.Conflicted) {
        await system.dialogStore.infoDialog({
          title: 'Installation error',
          message: 'Failed to create the integration: the integration already exists in this organization.'
        });
      }
      if (installationResult === ReturnUrlResult.ApprovalNeeded) {
        await system.dialogStore.infoDialog({
          title: 'Integration is pending approval',
          message:
            'An owner from the specified GitHub organization has been notified. They will need to complete this pending integration.'
        });
      }
      if (installationResult === ReturnUrlResult.Failed) {
        const message = url.searchParams?.get('error') as string;
        await system.dialogStore.infoDialog({
          title: 'Installation error',
          message: message
        });
      }
      if (installationResult === ReturnUrlResult.PartiallyConnected) {
        const org = orgStore.org(params.org_id!);
        await org.loadIntegrations();

        const id = url.searchParams?.get('integration_id') as string;
        const integration = org.integration(id);

        if (integration) {
          navigate(Routes.INSTALL_INTEGRATION, {
            ...params,
            integration_id: integration.model.id
          });
          return;
        }
      }

      navigate(Routes.MANAGE_INTEGRATIONS, params);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const org = await system.orgStore.getOrg(params.org_id);
      await org.loadIntegrations();
    })();
  }, [params.org_id]);

  const integrations = orgStore.org(params.org_id!)?.integrations;

  return (
    <OrgPageWidget activeItemKey={Routes.MANAGE_INTEGRATIONS}>
      <PageHeadingWidget title={'Integrations'} />
      <SearchableTableWidget<IntegrationRow>
        loading={!orgStore.org(params.org_id) || orgStore.org(params.org_id)?.integrations.loading}
        controlButtons={[
          {
            label: 'Add Integration',
            disabled: !isAllowedAddIntegration,
            action: () => {
              navigate(Routes.CREATE_INTEGRATION, params);
            }
          }
        ]}
        columns={[
          {
            key: 'integration',
            display: 'Integration',
            accessorSearch: (cell, row: IntegrationRow) => {
              if (row.integration.model.kind === IntegrationKind.GitHub) {
                return row.integration.getProperties<IntegrationKind.GitHub>().org;
              }
              return null;
            },
            accessor: ({ row }) => {
              if (row.integration.model.kind === IntegrationKind.GitHub) {
                return (
                  <S.Integration>
                    <S.Icon icon={'fa-brands fa-github' as any} />
                    <S.IntegrationLabel>GitHub</S.IntegrationLabel>
                  </S.Integration>
                );
              }
              if (row.integration.model.kind === IntegrationKind.AzureDevops) {
                return (
                  <S.Integration>
                    <S.IconImage icon={azure_devops} />
                    <S.IntegrationLabel>Azure DevOps</S.IntegrationLabel>
                  </S.Integration>
                );
              }
              return (
                <S.Integration>
                  <S.Icon icon="question" />
                  <S.IntegrationLabel>Unknown integration</S.IntegrationLabel>
                </S.Integration>
              );
            }
          },
          {
            key: 'created_at',
            display: 'Created Date'
          },
          {
            key: 'details',
            display: 'Details',
            accessor: ({ row }) => {
              if (row.integration.model.state === IntegrationState.Pending) {
                return (
                  <S.Row>
                    <Meta label="State" value={'Pending'} />
                  </S.Row>
                );
              }
              if (row.integration.model.state === IntegrationState.PartiallyConnected) {
                return (
                  <S.Row>
                    <Meta label="State" value={'Pending configuration'} />
                  </S.Row>
                );
              }

              if (row.integration.model.kind === IntegrationKind.GitHub) {
                const i = row.integration.getProperties<IntegrationKind.GitHub>();
                if (i.account_type === GithubAccountType.User) {
                  return (
                    <S.Row>
                      <Meta label="User account" value={i.org} />
                    </S.Row>
                  );
                }
                return (
                  <S.Row>
                    <Meta label="Organization" value={i.org} />
                  </S.Row>
                );
              }

              if (row.integration.model.kind === IntegrationKind.AzureDevops) {
                const i = row.integration.getProperties<IntegrationKind.AzureDevops>();
                return (
                  <S.Row>
                    <Meta label="Organization" value={i.organization} />
                    <Meta label="Project" value={i.project} />
                  </S.Row>
                );
              }
              return '';
            }
          },
          {
            key: 'row_action',
            display: '',
            shrink: true,
            accessor: (event) => {
              return (
                <S.ActionRow>
                  {event.row.integration.model.state === IntegrationState.Pending ? (
                    <S.RowButton
                      icon={'external-link-alt'}
                      label="Complete setup"
                      type={ButtonType.SECONDARY}
                      action={async (event2, loading) => {
                        loading(true);
                        const url = await event.row.integration.regenerateSetupUrl();
                        if (url) {
                          window.location.replace(url);
                        }
                        loading(false);
                      }}
                    />
                  ) : null}
                  {event.row.integration.model.state === IntegrationState.PartiallyConnected ? (
                    <S.RowButton
                      icon={'cog'}
                      label="Complete installation"
                      type={ButtonType.SECONDARY}
                      action={async (event2, loading) => {
                        navigate(Routes.INSTALL_INTEGRATION, {
                          ...params,
                          integration_id: event.row.integration.model.id
                        });
                      }}
                    />
                  ) : null}

                  {event.active ? <DeleteIntegrationButton event={event} integration={event.row.integration} /> : null}
                </S.ActionRow>
              );
            }
          }
        ]}
        rows={integrations?.items.map((i) => {
          return {
            key: i.model.id,
            integration: i,
            cells: {
              created_at: DateTime.fromISO(i.model.created_at).toFormat('yyyy LLL dd')
            }
          };
        })}
      />

      <RoutesSwitch>
        <Route path={relativeRoute(Routes.CREATE_INTEGRATION)} element={<AddIntegrationDialog />} />
        <Route path={relativeRoute(Routes.INSTALL_INTEGRATION)} element={<IntegrationInstallationDialogWidget />} />
      </RoutesSwitch>
    </OrgPageWidget>
  );
});

namespace S {
  export const Row = styled.div`
    display: flex;
    align-items: center;
    column-gap: 5px;
  `;

  export const ActionRow = styled.div`
    display: flex;
    align-items: center;
    padding-right: 40px;
    min-width: 300px;
  `;

  export const Integration = styled.div`
    display: flex;
    align-items: center;
    margin-right: 10px;
  `;

  export const Meta = styled.div`
    display: flex;
    align-items: center;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 4px;
    padding: 8px 10px;
  `;

  export const MetaLabel = styled.div`
    display: flex;
    opacity: 0.5;
  `;

  export const MetaValue = styled.div`
    display: flex;
    padding-left: 10px;
  `;

  export const IntegrationLabel = styled.div`
    padding-left: 10px;
  `;

  export const Icon = styled(FontAwesomeIcon)`
    font-size: 30px;
    color: white;
  `;

  export const IconImage = styled.div<{ icon: string }>`
    width: 30px;
    height: 30px;
    background-size: contain;
    background-image: url('${(p) => p.icon}');
    background-position: center center;
  `;

  export const RowButton = styled(ButtonWidget)`
    margin-left: 5px;

    &:first-of-type {
      margin-left: 0;
    }
  `;
}
